import { Injectable, inject, signal } from '@angular/core';
import { dfpHistoricalRequest } from '@interfaces/dfp/request/dfpIncomeStatementRequest';
import { dreAssumptionDFCResponse } from '@interfaces/dre/response/dreAssumptionDFCResponse';
import { DFPService } from '@services/http/dfp/dfp.service';
import { catchError, of, Subscription } from 'rxjs';

/**
 * DFC (fluxo de caixa indireto) dos anos realizados, mostrada na aba Demonstrativos (PBI 3875). Antes ela só existia
 * na tela de modelagem. O cálculo é do backend (`GET dfp-historical/dfc`, mesmo motor da modelagem); aqui só guardamos
 * as linhas. Fica fora do `dfp.component` para não engordar o componente.
 *
 * Falha não derruba a tela: sem a DFC, Balanço, DRE e Faturamento continuam iguais e o painel da DFC não aparece.
 * Só o time interno carrega (o endpoint do parceiro não expõe a DFC), então o chamador pula em `readOnly`.
 */
@Injectable({ providedIn: 'root' })
export class DfpCashFlowStore {
  private readonly dfpService = inject(DFPService);
  private subscription?: Subscription;

  /** Linhas da DFC por ano; vazio enquanto não carregou, quando falhou ou quando falta DRE ou Balanço. */
  readonly rows = signal<dreAssumptionDFCResponse[]>([]);

  /** A carga terminou (com ou sem linhas): só então a tela pode dizer que não há DFC. */
  readonly loaded = signal(false);

  /** Recarrega a DFC da empresa; cancela a carga anterior. */
  load(params: dfpHistoricalRequest): void {
    this.clear();
    this.subscription = this.dfpService
      .getDFPHistoricalCashFlow(params)
      // Falha não é "sem DFC": `loaded` fica falso e o módulo não aparece, em vez de dizer que a empresa não tem DFC.
      .pipe(catchError(() => of(undefined)))
      .subscribe(rows => {
        if (rows === undefined) return;
        this.rows.set(rows ?? []);
        this.loaded.set(true);
      });
  }

  /** Esquece a DFC (troca de empresa ou tela do parceiro). */
  clear(): void {
    this.subscription?.unsubscribe();
    this.rows.set([]);
    this.loaded.set(false);
  }
}
